import React, { useCallback, useMemo, useState } from 'react';
import {
  Column,
  GenericConfirmationModal,
  IconButton_IconSizes,
  Row,
  RowCentered,
  SectionHeaderRowSpaceBetween,
  SectionHeaderRowSpaceBetweenNoBorder,
  Title,
} from '@ydistri/ds';
import { Dropdown, DropdownProps, Popover, Space } from 'antd';
import { icons, IconType } from '../../components/icons/icons';
import { COLORS } from '../../styles/style';
import { useGetCalculationQuery } from './apiCalculationDetail';
import CalculationPotentialOverview from '../../components/global/HeaderPotentialOverview/CalculationPotentialOverview';
import { YPICK_LABEL } from '../../components/global/_constants';
import PrivateCalculationsIcon from '../Calculations/actions/PrivateCalculationsIcon';
import LoadIntoTemplateAction from '../Calculations/actions/LoadIntoTemplateAction';
import LoadIntoTemplateModal from '../Calculations/modals/LoadIntoTemplateModal';
import EditCalculationModal from './EditCalculationModal';
import {
  PatchCalculationPayload,
  useDeleteCalculationMutation,
  usePatchCalculationMutation,
} from '../Calculations/apiCalculations';
import { CalculationMode, CalculationStatus, CalculationType } from '@ydistri/api-sdk';
import { addToast } from '@ydistri/utils';
import MenuSubpages from '../../components/menu/MenuSubpages';
import { RoutingPage } from '../../routes/routerSlice';
import { CalculationDetailSubpages } from '../Calculations/calculationsLib';
import useSubpage from '../../hooks/useSubpage';
import MoreIconButton from '../../components/buttons/MoreIconButton';
import { SubmenuOption } from '../Configuration/ConfigurationHeader';
import ProductionCalculationIcon from '../Calculations/actions/ProductionCalculationIcon';
import { useUser } from '../../hooks/useUser';
import { styled } from 'styled-components';
import CalculationTitleItem from './components/CalculationTitleItem';
import { useApplicationData } from '../../hooks/useApplicationData';
import { AvailableModals, openModal } from '../Modals/modalSlice';
import ConfigurationExceptions from '../Configuration/ConfigurationCategories/ConfigurationExceptions';
import YPickExportButton from './YPickExportButton';
import CustomExportButton from './CustomExportButton';
import RecomputeStatisticsButton from '../Calculations/RecomputeStatisticsButton';
import { MdCopyAll } from 'react-icons/md';
import CreateCalculationWithDefaultsModal from '../Calculations/components/CreateCalculationWithDefaultsModal/CreateCalculationWithDefaultsModal';
import { ItemType } from 'antd/es/menu/interface';
import { useAppDispatch } from '../../store';
import { router } from '../../routes/router';
import { useStableParams } from '../../routes/useStableParams';
import { useGetCalculationConfigurationQuery } from '../../apis/apiGlobalConfiguration';
import ManualCalculationImportAction from './Redistribution/parts/listing/actions/ManualCalculationImportAction';
import EditRedistributionStatusButtons from './Editation/EditRedistributionStatusButtons';
import { formatDateTime } from '@ydistri/generic-utils';
import { useTemplateOrCalculationWithValue } from '../../hooks/useTemplateOrCalculationWithValue';
import { skipToken } from '@reduxjs/toolkit/query';
import { useApplicationConfiguration } from '../../hooks/useApplicationConfiguration';
import { shouldShowCustomExportEntry } from './CustomExport/customExportLib';
import { useGetLatestCustomExportRun } from './CustomExport/useGetLatestCustomExportRun';

enum CalculationAction {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  CREATE_MERGED = 'CREATE_MERGED',
}

const CalculationTitle = styled(Title)`
  max-width: 60rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const getProductionTooltip = (editingCalculation: boolean, isProduction: boolean): string => {
  if (editingCalculation) {
    return `This calculation is manually edited. It can not be set as production before recomputing statistics.`;
  }
  return isProduction
    ? `Remove "production" flag or change execution date`
    : `Mark this calculation as "production", meaning it was really redistributed.`;
};

const CalculationHeader: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const dispatch = useAppDispatch();
  const subpage = useSubpage();
  const user = useUser();
  const { projectShortName } = useStableParams();
  const { departments: hasDepartments } = useApplicationData();
  const { isCustomExportEnabled } = useApplicationConfiguration();

  const { data: calculationData } = useGetCalculationQuery(
    templateOrCalculation.hasValidValue ? templateOrCalculation.id : skipToken,
  );
  const { data: calculationConfigurationData } = useGetCalculationConfigurationQuery(
    templateOrCalculation.value,
  );
  const [openedActionModal, setOpenedActionModal] = useState<CalculationAction>();
  const [showLoadIntoTemplateModal, setShowLoadIntoTemplateModal] = React.useState(false);
  const [editCalculation, { isLoading }] = usePatchCalculationMutation();
  const [deleteCalculation] = useDeleteCalculationMutation();

  const isPrivate = calculationData?.type === CalculationType.Private;
  const isProduction = calculationData?.type === CalculationType.Production;
  const { data: latestCustomExportRun } = useGetLatestCustomExportRun(
    templateOrCalculation,
    templateOrCalculation.hasValidValue && isProduction ? undefined : skipToken,
  );

  const manualCalculation = calculationData?.mode === CalculationMode.Manual;
  const mergedCalculation = calculationData?.mode === CalculationMode.Merged;
  const submergedCalculation = calculationData?.type === CalculationType.Submerged;
  const editingCalculation = calculationData?.status === CalculationStatus.Editing;
  const hasRequestList = !!calculationConfigurationData?.ptRequestListEntityList;

  const showLoadIntoTemplateModalHandler = useCallback(
    () => setShowLoadIntoTemplateModal(true),
    [],
  );
  const closeLoadIntoTemplateModal = useCallback(() => setShowLoadIntoTemplateModal(false), []);

  const closeOpenedActionModal = useCallback(() => {
    setOpenedActionModal(undefined);
  }, []);

  const deleteCalculationHandler = useCallback(() => {
    if (templateOrCalculation.type === 'Calculation' && projectShortName) {
      router.navigate(`/${projectShortName}/calculations`);
      deleteCalculation(templateOrCalculation.id)
        .unwrap()
        .then(() => {
          dispatch(addToast({ message: 'Calculation deleted' }));
        })
        .catch(() => {
          dispatch(addToast({ message: 'Calculation was not deleted', isError: true }));
        });
    }
  }, [
    projectShortName,
    templateOrCalculation.type,
    templateOrCalculation.id,
    deleteCalculation,
    dispatch,
  ]);

  const calculationOptionDropdownItems: DropdownProps['menu'] = useMemo(() => {
    const menuItems: ItemType[] = [
      {
        label: 'Edit calculation',
        key: '1',
        icon: icons[IconType.EDIT_OUTLINED],
        onClick: () => {
          setOpenedActionModal(CalculationAction.EDIT);
        },
        style: {
          backgroundColor: COLORS.PRIMARY_LIGHT,
        },
      },
    ];

    if (calculationData?.mode === CalculationMode.Merged) {
      menuItems.push({
        label: 'Create using this merged calculation...',
        key: 'createNewMergedBasedOnThis',
        icon: <MdCopyAll size={IconButton_IconSizes.xSmall} />,
        onClick: () => {
          setOpenedActionModal(CalculationAction.CREATE_MERGED);
        },
      });
    }

    menuItems.push(
      {
        type: 'divider',
      },
      {
        label: 'Delete',
        key: '2',
        icon: icons[IconType.DELETE_OUTLINED],
        onClick: () => {
          setOpenedActionModal(CalculationAction.DELETE);
        },
        danger: true,
      },
    );

    return {
      items: menuItems,
    };
  }, [calculationData?.mode]);

  const dropdownTrigger: DropdownProps['trigger'] = useMemo(() => ['click'], []);
  const dropdownAlign: DropdownProps['align'] = useMemo(() => ({ offset: [0, 0] }), []);

  const togglePrivate = useCallback(() => {
    const payload: PatchCalculationPayload = {
      calculationId: templateOrCalculation.id,
      data: {
        calculationType: isPrivate ? CalculationType.Simulation : CalculationType.Private,
      },
    };
    editCalculation(payload);
  }, [editCalculation, isPrivate, templateOrCalculation.id]);

  const toggleProduction = useCallback(() => {
    dispatch(
      openModal({
        type: isProduction ? AvailableModals.UNSET_PRODUCTION : AvailableModals.SET_PRODUCTION,
        data: {
          calculationId: templateOrCalculation.id,
        },
      }),
    );
  }, [dispatch, isProduction, templateOrCalculation.id]);

  const submenuOptions: SubmenuOption[] = useMemo(() => {
    const options: SubmenuOption[] = [];
    const configurationsDisabled = manualCalculation || mergedCalculation;

    options.push(
      {
        value: CalculationDetailSubpages.CATEGORIES,
        label: 'Categories',
        disabled: configurationsDisabled,
      },
      {
        value: CalculationDetailSubpages.REGIONS,
        label: 'Regions',
        disabled: configurationsDisabled,
      },
      {
        value: CalculationDetailSubpages.STORES_AND_DEPARTMENTS,
        label: hasDepartments ? 'Stores and departments' : 'Stores',
        disabled: configurationsDisabled,
      },
      {
        value: CalculationDetailSubpages.SKUCLASSES,
        label: 'SKU classes',
        disabled: configurationsDisabled,
      },
      {
        value: CalculationDetailSubpages.PRODUCTLISTS,
        label: 'Product lists',
        disabled: configurationsDisabled,
      },
      {
        value: CalculationDetailSubpages.BRANDS,
        label: 'Brands',
        disabled: configurationsDisabled,
        appendDivider: !user?.isSuperUser,
      },
    );

    if (user?.isSuperUser) {
      options.push({
        value: CalculationDetailSubpages.ADVANCED,
        label: 'Advanced',
        disabled: configurationsDisabled,
        appendDivider: true,
      });
    }

    options.push(
      {
        value: CalculationDetailSubpages.DRILLDOWN,
        label: 'Drill down',
      },
      {
        value: CalculationDetailSubpages.REDISTRIBUTION,
        label: 'Redistribution',
      },
      {
        value: CalculationDetailSubpages.STATISTICS,
        label: 'Statistics',
        appendDivider: isProduction && !hasRequestList,
      },
      {
        value: CalculationDetailSubpages.REQUEST_LIST_STATISTICS,
        label: 'Request list',
        isVisible: hasRequestList,
        appendDivider: isProduction,
      },
    );

    if (calculationData?.isPap !== undefined) {
      options.push(
        {
          value: CalculationDetailSubpages.EVALUATION,
          label: 'Evaluation',
          isVisible: isProduction && !calculationData.isPap,
        },
        {
          value: CalculationDetailSubpages.PICKING_RESULTS_EXECUTION,
          label: `Evaluation (${YPICK_LABEL})`,
          isVisible: isProduction && calculationData.isPap,
        },
      );
    }

    if (
      shouldShowCustomExportEntry({
        isCustomExportEnabled,
        isProduction,
        hasRunHistory: !!latestCustomExportRun,
      })
    ) {
      options.push({
        value: CalculationDetailSubpages.CUSTOM_EXPORT,
        label: 'Export',
      });
    }

    return options;
  }, [
    manualCalculation,
    mergedCalculation,
    hasDepartments,
    user,
    isProduction,
    hasRequestList,
    calculationData,
    isCustomExportEnabled,
    latestCustomExportRun,
  ]);

  const calculationTitle = `#${calculationData?.id} ${calculationData?.title}`;

  const canBeProduction = !submergedCalculation;
  const canBePrivate = !submergedCalculation;
  const canBeLoadIntoTemplate = !mergedCalculation;

  const productionTooltip = getProductionTooltip(editingCalculation, isProduction);

  return (
    <>
      <SectionHeaderRowSpaceBetween>
        <RowCentered $gap="1rem">
          <Column $maxWidth="60rem">
            <RowCentered>
              <Popover content={calculationTitle}>
                <CalculationTitle $size="large">{calculationTitle}</CalculationTitle>
              </Popover>
            </RowCentered>
            <RowCentered $gap="1rem" $flexWrap="nowrap" $maxWidth="60rem">
              {calculationData?.created && (
                <CalculationTitleItem
                  label="Created at"
                  labelWidth="6rem"
                  content={formatDateTime(calculationData.created)}
                  contentWidth="9rem"
                />
              )}
              {calculationData?.mode === CalculationMode.Computed && (
                <CalculationTitleItem
                  label="Template"
                  labelWidth="5rem"
                  content={
                    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- WARNING !!! Source template actually CAN be null, in case template is deleted */
                    calculationData?.sourceTemplate?.title ?? 'deleted template'
                  }
                  contentWidth={
                    /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- WARNING !!! Source template actually CAN be null, in case template is deleted */
                    calculationData?.description ? '20rem' : undefined
                  }
                />
              )}
              {calculationData?.description && (
                <CalculationTitleItem
                  label="Description"
                  labelWidth="6rem"
                  content={calculationData.description}
                  contentWidth="20rem"
                />
              )}
            </RowCentered>
          </Column>
          <Dropdown
            trigger={dropdownTrigger}
            menu={calculationOptionDropdownItems}
            align={dropdownAlign}
          >
            <div>
              <MoreIconButton />
            </div>
          </Dropdown>
        </RowCentered>
        <RowCentered $padding={1}>
          <Space>
            {calculationData && (
              <CalculationPotentialOverview
                calculationMode={calculationData.mode}
                calculationStatus={calculationData.status}
                overlappingCalculations={calculationData.overlappingCalculations}
              />
            )}
            {user?.isSuperUser && canBePrivate && (
              <PrivateCalculationsIcon
                size="small"
                loading={isLoading}
                tooltip={
                  isProduction
                    ? `Production calculation can not be set as private`
                    : `Make ${isPrivate ? 'public' : 'private'}`
                }
                onClick={togglePrivate}
                value={isPrivate}
                disabled={isProduction}
              />
            )}
            <YPickExportButton />
            <CustomExportButton />
            {canBeProduction && (
              <ProductionCalculationIcon
                size="small"
                loading={isLoading}
                tooltip={productionTooltip}
                onClick={toggleProduction}
                value={isProduction}
                disabled={editingCalculation}
              />
            )}
            {canBeLoadIntoTemplate && (
              <LoadIntoTemplateAction
                size="small"
                tooltipPosition="left"
                onClick={showLoadIntoTemplateModalHandler}
                disabled={manualCalculation}
              />
            )}
          </Space>
        </RowCentered>
      </SectionHeaderRowSpaceBetween>
      <SectionHeaderRowSpaceBetweenNoBorder>
        <MenuSubpages
          routingPage={RoutingPage.CALCULATIONS}
          submenuOptions={submenuOptions}
          selectedOption={subpage}
        />
        {subpage === CalculationDetailSubpages.CATEGORIES && <ConfigurationExceptions />}
        <Row $gap="1rem">
          <EditRedistributionStatusButtons calculationId={templateOrCalculation.id} />
          {manualCalculation && (
            <ManualCalculationImportAction calculation={calculationData} navigateOnClick={true} />
          )}
          {(manualCalculation || user?.isSuperUser) && (
            <RecomputeStatisticsButton
              calculationStatus={calculationData?.status}
              calculationType={calculationData?.type}
            />
          )}
        </Row>
      </SectionHeaderRowSpaceBetweenNoBorder>
      {openedActionModal === CalculationAction.EDIT && calculationData && (
        <EditCalculationModal
          open={true}
          onCancel={closeOpenedActionModal}
          calculationId={calculationData.id}
        />
      )}
      {openedActionModal === CalculationAction.DELETE && calculationData && (
        <GenericConfirmationModal
          title="Delete calculation"
          message={`Calculation '${calculationData.title}' will be deleted. Continue? `}
          onConfirmed={deleteCalculationHandler}
          onCanceled={closeOpenedActionModal}
        />
      )}
      {openedActionModal === CalculationAction.CREATE_MERGED && calculationData && (
        <CreateCalculationWithDefaultsModal
          calculation={calculationData}
          onClose={closeOpenedActionModal}
        />
      )}

      {showLoadIntoTemplateModal && calculationData && (
        <LoadIntoTemplateModal
          calculationId={calculationData.id}
          calculationTitle={calculationData.title}
          onCanceled={closeLoadIntoTemplateModal}
        />
      )}
    </>
  );
};

const MemoCalculationHeader = React.memo(CalculationHeader);
MemoCalculationHeader.displayName = 'CalculationHeader';

export default MemoCalculationHeader;
